import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BarChart3, Database, Loader2, Info, Download, FileText, Mail, Send } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { track } from '../lib/analytics';
import AdminStatusStrip from '../components/AdminStatusStrip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only Raport wzorców procesowych.
 *
 * Pulls aggregated counts from /api/admin/process-patterns. NEVER renders
 * raw text — bucket labels and counts only. Backend guarantees no PII
 * lands in the response payload.
 *
 * Doctrine: "Raw data expires. Process intelligence compounds."
 */
const ProcessPatterns = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/process-patterns`, {
        params: { days },
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się pobrać raportu.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const exportCsv = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/process-patterns/export.csv`, {
        params: { days },
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hussarai-process-patterns-${days}d.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się wyeksportować CSV.');
    }
  }, [days]);

  const downloadBrief = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/process-patterns/brief.pdf`, {
        params: { days },
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hussarai-process-patterns-brief-${days}d.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Nie udało się pobrać briefu PDF.');
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="hussar-landing" data-testid="process-patterns-page">
      <header className="hussar-landing-header">
        <div className="hussar-landing-header-content">
          <Link to="/admin/training-center" className="hussar-tc-back" data-testid="pp-back">
            <ArrowLeft className="w-4 h-4" /> Training Center
          </Link>
          <div className="hussar-landing-header-actions">
            <Link to="/admin/data-governance" className="hussar-tc-back"
                  data-testid="pp-nav-data-governance">
              Data Governance
            </Link>
            <span className="hussar-tc-meta" data-testid="pp-admin-badge">Admin · Raport wzorców</span>
          </div>
        </div>
      </header>

      <main className="hussar-agenci-main">
        <AdminStatusStrip />
        <section className="hussar-agenci-hero" data-testid="pp-hero">
          <div className="hussar-agenci-eyebrow">
            <BarChart3 className="w-3 h-3 inline-block mr-1" /> RAPORT WZORCÓW PROCESOWYCH
          </div>
          <h1 className="hussar-agenci-h1" data-testid="pp-h1">Raport wzorców procesowych</h1>
          <p className="hussar-agenci-subhead" data-testid="pp-subhead">
            Agregowane wzorce z procesów HussarAi. Tylko etykiety kategorii i liczniki.
            Brak imion, adresów, instytucji i cech chronionych.
          </p>
          <p className="hussar-tc-meta" data-testid="pp-doctrine">
            „Raw data expires. Process intelligence compounds.”
          </p>
        </section>

        <section className="hussar-pol-op-section" data-testid="pp-controls">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="hussar-form-label" htmlFor="pp-days">Zakres (dni):</label>
            {[7, 30, 90, 180, 365].map((n) => (
              <Button key={n}
                      size="sm"
                      variant={days === n ? 'default' : 'outline'}
                      onClick={() => setDays(n)}
                      data-testid={`pp-range-${n}`}>
                {n}
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={load} data-testid="pp-refresh">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Odśwież'}
            </Button>
            <Button size="sm" onClick={exportCsv} data-testid="pp-export-csv">
              <Download className="w-3 h-3 mr-1" /> Eksport CSV
            </Button>
            <Button size="sm" onClick={downloadBrief} data-testid="pp-download-brief">
              <FileText className="w-3 h-3 mr-1" /> Pobierz brief PDF
            </Button>
            <SendBriefButton days={days} onError={setError} />
          </div>
          <p className="hussar-tc-meta" style={{ marginTop: '8px' }} data-testid="pp-export-helper">
            CSV i PDF zawierają wyłącznie zagregowane kategorie procesowe. Nie zawierają danych osobowych ani treści spraw.
          </p>
          <p className="hussar-tc-meta" style={{ marginTop: '4px' }} data-testid="pp-send-helper">
            Email zawiera wyłącznie opis i załączony brief PDF. Brief opiera się na zagregowanych kategoriach procesowych i nie zawiera danych osobowych ani treści spraw.
          </p>
        </section>

        {error && (
          <section className="hussar-pol-op-section" data-testid="pp-error">
            <Card className="hussar-compliance-error"><p>{error}</p></Card>
          </section>
        )}

        {loading && !data && (
          <section className="hussar-pol-op-section" data-testid="pp-loading">
            <Card className="hussar-tc-card">
              <p><Loader2 className="w-4 h-4 inline-block mr-1 animate-spin" /> Ładuję raport…</p>
            </Card>
          </section>
        )}

        {data && (
          <>
            <section className="hussar-pol-op-section" data-testid="pp-summary">
              <Card className="hussar-tc-card">
                <div className="hussar-compliance-output-badge"><Database className="w-4 h-4" /> Podsumowanie</div>
                <p style={{ marginTop: '8px' }}>
                  Wszystkich zdarzeń: <strong data-testid="pp-total">{data.totalEvents}</strong>
                  {' · '}
                  Zakres: ostatnie {data.rangeDays} dni
                </p>
                <p className="hussar-tc-meta" style={{ marginTop: '8px' }}>
                  <Info className="w-3 h-3 inline-block mr-1" /> {data.note}
                </p>
              </Card>
            </section>

            <BucketCard title="Typy workflow"            data-testid="pp-workflow"   items={data.workflowTypes} />
            <BucketCard title="Kategorie braków"         data-testid="pp-missing"    items={data.missingInfoCategories} />
            <BucketCard title="Kategorie dokumentów"     data-testid="pp-documents"  items={data.documentCategories} />
            <BucketCard title="Kategorie terminów"       data-testid="pp-deadlines"  items={data.deadlineCategories} />
            <BucketCard title="Sekcje wygenerowanego wyniku" data-testid="pp-sections" items={data.outputSections} />
            <BucketCard title="Status weryfikacji przez człowieka" data-testid="pp-review" items={data.humanReviewStatus} />
            <BucketCard title="Sygnały do weryfikacji"   data-testid="pp-integrity"   items={data.integritySignalCategories} emptyHint="(brak sygnałów w okresie)" />
            <BucketCard title="Kategorie korekt"         data-testid="pp-corrections" items={data.correctionCategories} emptyHint="(brak korekt w okresie)" />
          </>
        )}

        <footer className="hussar-pol-op-footer" data-testid="pp-footer">
          <p>HussarAi · Raport wzorców procesowych · Admin only</p>
          <p className="hussar-pol-op-footer-meta">
            HussarAi ocenia kompletność procesu, nie wartość człowieka.
          </p>
        </footer>
      </main>
    </div>
  );
};

const BucketCard = ({ title, items, emptyHint, ...rest }) => (
  <section className="hussar-pol-op-section" {...rest}>
    <Card className="hussar-tc-card">
      <h3 className="hussar-agenci-h2" style={{ fontSize: '18px' }}>{title}</h3>
      {(!items || items.length === 0) ? (
        <p className="hussar-tc-meta" style={{ marginTop: '12px' }}>
          {emptyHint || '(brak danych w okresie)'}
        </p>
      ) : (
        <ul className="hussar-pol-op-bullets" style={{ marginTop: '12px' }}>
          {items.map((it) => (
            <li key={it.label}>
              <code>{it.label}</code> · <strong>{it.count}</strong>
            </li>
          ))}
        </ul>
      )}
    </Card>
  </section>
);

/* ---------------- Send brief by email modal ---------------- */
const initialSendForm = {
  to: '', language: 'pl', recipientType: 'administration', customNote: '',
};

const SendBriefButton = ({ days, onError }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialSendForm);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'sent' | 'sent_pending_email' | 'failed' | null
  const [msg, setMsg] = useState('');
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => { setForm(initialSendForm); setStatus(null); setMsg(''); };

  const openModal = () => {
    track('process_patterns_send_brief_opened', { days });
    reset();
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.to.trim())) {
      setMsg('Podaj prawidłowy adres email.');
      return;
    }
    setSubmitting(true);
    track('process_patterns_send_brief_submitted', { days, language: form.language });
    try {
      const r = await axios.post(`${API}/admin/process-patterns/send-brief`,
        { days, ...form, customNote: form.customNote || null },
        { withCredentials: true });
      setStatus(r.data.status);
      track('process_patterns_send_brief_success', { status: r.data.status });
    } catch (err) {
      setStatus('failed');
      setMsg(err?.response?.data?.detail || 'Nie udało się wysłać briefu. Sprawdź adres email lub konfigurację wysyłki.');
      track('process_patterns_send_brief_failed', {});
      if (onError) onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={openModal}
              data-testid="pp-send-brief-open">
        <Mail className="w-3 h-3 mr-1" /> Wyślij brief emailem
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent data-testid="pp-send-brief-modal">
          <DialogHeader>
            <DialogTitle>Wyślij brief PDF</DialogTitle>
            <DialogDescription>
              Brief opiera się wyłącznie na zagregowanych kategoriach procesowych. Bez danych osobowych ani treści spraw.
            </DialogDescription>
          </DialogHeader>

          {status === 'sent' && (
            <div className="hussar-compliance-output-badge" data-testid="pp-send-brief-success">
              Brief został wysłany.
            </div>
          )}
          {status === 'sent_pending_email' && (
            <div className="hussar-compliance-output-badge" data-testid="pp-send-brief-pending">
              Brief został przygotowany, ale wysyłka email wymaga konfiguracji lub ponowienia.
            </div>
          )}

          {!status && (
            <form onSubmit={submit} className="hussar-compliance-form">
              <div className="hussar-cb-field">
                <label className="hussar-form-label">Adres email odbiorcy</label>
                <Input type="email" value={form.to}
                       onChange={(e) => upd('to', e.target.value)}
                       placeholder="naczelnik@urzad.gov.pl"
                       data-testid="pp-send-brief-to" required />
              </div>
              <div className="hussar-cb-field">
                <label className="hussar-form-label">Język wiadomości</label>
                <Select value={form.language} onValueChange={(v) => upd('language', v)}>
                  <SelectTrigger data-testid="pp-send-brief-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pl">Polski</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="hussar-cb-field">
                <label className="hussar-form-label">Typ odbiorcy</label>
                <Select value={form.recipientType} onValueChange={(v) => upd('recipientType', v)}>
                  <SelectTrigger data-testid="pp-send-brief-recipient"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administration">Administracja</SelectItem>
                    <SelectItem value="strategic_company">Firma strategiczna</SelectItem>
                    <SelectItem value="partner">Partner</SelectItem>
                    <SelectItem value="other">Inny</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="hussar-cb-field">
                <label className="hussar-form-label">Krótka notatka opcjonalna</label>
                <Textarea rows={3} value={form.customNote}
                          onChange={(e) => upd('customNote', e.target.value)}
                          placeholder="np. „Załączam materiał kontekstowy do rozmowy w przyszłym tygodniu.”"
                          data-testid="pp-send-brief-note" />
              </div>
              {msg && <p className="hussar-compliance-error" data-testid="pp-send-brief-error">{msg}</p>}
              <DialogFooter>
                <Button type="submit" disabled={submitting}
                        className="hussar-btn-primary"
                        data-testid="pp-send-brief-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {submitting ? 'Wysyłanie…' : 'Wyślij brief'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProcessPatterns;
